import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Car, LogOut, Phone, Mail, Home } from "lucide-react";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import CalendarTab from "../components/admin/CalendarTab";

export default function DriverDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!user) { navigate("/driver/login"); return; }
    if (user.role && user.role !== "driver" && user.role !== "admin" && user.role !== "super_admin") {
      // Regular customers don't belong here.
      navigate("/dashboard");
      return;
    }
    axios.get(`${API}/driver/me`)
      .then((res) => { if (alive) setDriver(res.data); })
      .catch((err) => {
        if (alive) {
          toast.error(err.response?.data?.detail || "Driver profile not found");
          if (err.response?.status === 401) navigate("/driver/login");
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user, navigate]);

  if (loading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="spinner" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="driver-dashboard">
      <header className="border-b border-white/5 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-2xl font-bold logo-text">TN</Link>
            <div className="hidden sm:block">
              <p className="text-white text-sm font-semibold">Driver Dashboard</p>
              {driver && <p className="text-white/40 text-xs">{driver.name} {driver.surname} · {driver.email}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {driver?.mobile && (
              <a href={`tel:${driver.mobile}`} className="text-white/60 hover:text-[#C5A059] text-xs flex items-center gap-1" data-testid="driver-phone-link">
                <Phone className="w-3.5 h-3.5" /> {driver.mobile}
              </a>
            )}
            <Link to="/" className="text-white/60 hover:text-white"><Home className="w-4 h-4" /></Link>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/driver/login"); }} className="text-white/60 hover:text-red-400" data-testid="driver-logout">
              <LogOut className="w-4 h-4 mr-1" /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {driver && (
          <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4 mb-6 flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-3">
              {driver.picture_url ? (
                <img src={driver.picture_url} alt={driver.name} className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#C5A059]/15 flex items-center justify-center">
                  <Car className="w-5 h-5 text-[#C5A059]" />
                </div>
              )}
              <div>
                <p className="text-white font-semibold">{driver.name} {driver.surname}</p>
                <p className="text-white/40 text-xs flex items-center gap-3">
                  <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {driver.email}</span>
                  <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {driver.mobile}</span>
                </p>
              </div>
            </div>
          </div>
        )}
        <CalendarTab scope="driver" />
      </main>
    </div>
  );
}
